"use client";

import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";

const OrderDetailsModal = ({ visible, order, onClose, onUpdateStatus }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  if (!order) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", text: "Pending" },
      confirmed: { color: "info", text: "Confirmed" },
      processing: { color: "primary", text: "Processing" },
      shipped: { color: "success", text: "Shipped" },
      delivered: { color: "success", text: "Delivered" },
      cancelled: { color: "danger", text: "Cancelled" },
      refunded: { color: "secondary", text: "Refunded" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return `$${Number.parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatShippingAddress = (shippingAddress) => {
    if (!shippingAddress) return "N/A";

    // If it's already a formatted string
    if (typeof shippingAddress === "string") {
      return shippingAddress;
    }

    // If it's an object, format it
    if (typeof shippingAddress === "object") {
      const { first_name, last_name, street, city, country } = shippingAddress;
      const parts = [];

      if (first_name || last_name) {
        parts.push(`${first_name || ""} ${last_name || ""}`.trim());
      }
      if (street) parts.push(street);
      if (city) parts.push(city);
      if (country) parts.push(country);

      return parts.filter(Boolean).join(", ") || "N/A";
    }

    return "N/A";
  };

  const handleStatusUpdate = async (newStatus) => {
    if (newStatus !== order.status) {
      if (
        window.confirm(
          `Change order status to ${
            newStatus.charAt(0).toUpperCase() + newStatus.slice(1)
          }?`
        )
      ) {
        setIsUpdating(true);
        try {
          await onUpdateStatus(order.id, newStatus);
        } catch (error) {
          console.error("Error updating status:", error);
        } finally {
          setIsUpdating(false);
        }
      }
    }
  };

  const statusOptions = [
    "pending",
    "confirmed",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
    "refunded",
  ];

  return (
    <Modal
      isOpen={visible}
      toggle={onClose}
      centered
      size="lg"
      className="order-details-modal"
    >
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center">
          <i className="ri-shopping-cart-line me-2 text-primary"></i>
          Order Details
          <Badge color="light" className="ms-2">
            #{order.order_number || order.id}
          </Badge>
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="order-details">
          {/* Order Header */}
          <Row className="mb-4">
            <Col md={8}>
              <div className="d-flex align-items-start">
                <div>
                  <h5 className="mb-2">
                    Order #{order.order_number || order.id}
                  </h5>
                  <p className="text-muted mb-2">
                    Placed on {formatDate(order.created_at)}
                  </p>
                  <div className="d-flex gap-2 align-items-center">
                    {getStatusBadge(order.status)}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="order-total">
                <h4 className="text-success mb-0">
                  {formatCurrency(order.total || order.total_amount)}
                </h4>
                <small className="text-muted">Total Amount</small>
              </div>
            </Col>
          </Row>

          {/* Customer Information */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-user-line me-2"></i>
                  Customer Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Name:</strong> {order.user?.name || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Email:</strong> {order.user?.email || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Phone:</strong> {order.user?.phone || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>User Type:</strong>{" "}
                  {order.user?.account_type_id === 1
                    ? "Influencer"
                    : order.user?.account_type_id === 2
                    ? "Business"
                    : "User"}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-information-line me-2"></i>
                  Order Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Order ID:</strong> #{order.id}
                </div>
                <div className="info-item mb-2">
                  <strong>Order Number:</strong> {order.order_number || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Total Quantity:</strong> {order.total_quantity || 0}
                </div>
                <div className="info-item mb-2">
                  <strong>Created:</strong> {formatDate(order.created_at)}
                </div>
                <div className="info-item mb-2">
                  <strong>Last Updated:</strong> {formatDate(order.updated_at)}
                </div>
              </div>
            </Col>
          </Row>

          {/* Shipping Information */}
          <Row className="mb-4">
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-map-pin-line me-2"></i>
                  Shipping Address
                </h6>
                <p className="mb-0">
                  {formatShippingAddress(
                    order.shipping_address || order.formatted_shipping_address
                  )}
                </p>
              </div>
            </Col>
          </Row>

          {/* Order Items */}
          <Row className="mb-4">
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-shopping-bag-line me-2"></i>
                  Order Items ({order.order_items?.length || 0})
                </h6>
                {order.order_items && order.order_items.length > 0 ? (
                  order.order_items.map((item, index) => (
                    <div
                      key={index}
                      className="order-item d-flex mb-3 p-3 border rounded"
                    >
                      <div className="flex-grow-1">
                        <h6 className="mb-1">
                          {item.product_name || "Product"}
                        </h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">
                            {formatCurrency(item.product_price || 0)} ×{" "}
                            {item.quantity || 1}
                          </span>
                          <span className="fw-bold">
                            {formatCurrency(
                              item.total_price ||
                                item.product_price * item.quantity ||
                                0
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No items found</p>
                )}
              </div>
            </Col>
          </Row>

          {/* Order Summary */}
          <Row className="mb-4">
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-calculator-line me-2"></i>
                  Order Summary
                </h6>
                <div className="summary-container">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  {order.tax && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Tax:</span>
                      <span>{formatCurrency(order.tax)}</span>
                    </div>
                  )}
                  <hr />
                  <div className="d-flex justify-content-between">
                    <strong>Total:</strong>
                    <strong className="text-success">
                      {formatCurrency(order.total || order.total_amount)}
                    </strong>
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          {/* Order Timeline */}
          <Row className="mb-4">
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-time-line me-2"></i>
                  Order Timeline
                </h6>
                <div className="timeline">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Order Created</span>
                    <span className="text-muted">
                      {formatDate(order.created_at)}
                    </span>
                  </div>
                  {order.updated_at !== order.created_at && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Last Updated</span>
                      <span className="text-muted">
                        {formatDate(order.updated_at)}
                      </span>
                    </div>
                  )}
                  {order.shipped_at && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Shipped</span>
                      <span className="text-muted">
                        {formatDate(order.shipped_at)}
                      </span>
                    </div>
                  )}
                  {order.delivered_at && (
                    <div className="d-flex justify-content-between mb-2">
                      <span>Delivered</span>
                      <span className="text-muted">
                        {formatDate(order.delivered_at)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          {/* Additional Notes */}
          {order.notes && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-file-text-line me-2"></i>
                    Notes
                  </h6>
                  <p className="mb-0">{order.notes}</p>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="d-flex justify-content-between w-100">
          <div className="status-actions">
            <h6 className="mb-2">Update Status:</h6>
            <div className="d-flex gap-2 flex-wrap">
              {statusOptions
                .filter((status) => status !== order.status)
                .map((status) => (
                  <Button
                    key={status}
                    color={
                      status === "delivered"
                        ? "success"
                        : status === "cancelled"
                        ? "danger"
                        : status === "shipped"
                        ? "info"
                        : "secondary"
                    }
                    size="sm"
                    onClick={() => handleStatusUpdate(status)}
                    disabled={isUpdating}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
            </div>
          </div>
          <div>
            <Button color="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default OrderDetailsModal;
